import { Box, Button, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import StyledBackButton from '../Common/StyledBackButton';
import { ICoachServicePackageCardProps } from './CoachServicePackageEditCard';
import { useAppSelector } from '../../redux/hooks';
import LoadingButton from '../Common/LoadingButton';
import { resetCoachServicePackages } from '../../redux/coach/coachSlice';
import { BookingTypeEnum } from '../../redux/booking/bookingSlice';
import scrollbarStyles from '../../utils/styles';
import { formatDuration } from '../../utils/utils';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

export default function CoachServicePackageCard({
  id,
  title,
  bookingType,
  rate,
  description,
  duration,
  onEdit,
  onDelete,
  backgroundColor,
  hasBookings,
  setDeleteModePackageId,
  deleteModePackageId,
  setEditModePackageId,
  editModePackageId,
}: Readonly<ICoachServicePackageCardProps>) {
  const { t } = useTranslation();
  const [deleteService, setDeleteService] = useState(false);
  const {
    deleteCoachServicePackageLoading,
    deletingPackageSuccess,
    createCoachServicePackagesLoading,
    updateCoachServicePackageLoading,
  } = useAppSelector((state) => state.coach);
  const dispatch = useDispatch();
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  useEffect(() => {
    if (deleteModePackageId !== id) {
      setDeleteService(false);
    }
  }, [deleteModePackageId]);

  useEffect(() => {
    if (deletingPackageSuccess) {
      setDeleteService(false);
      dispatch(resetCoachServicePackages());
    }
  }, [deletingPackageSuccess]);

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  const handleClickDelete = () => {
    setDeleteService(true);
    if (setEditModePackageId) {
      setEditModePackageId(null);
    }
    if (setDeleteModePackageId) {
      setDeleteModePackageId(id);
    }
  };

  const handleCancel = () => {
    setDeleteService(false);
  };

  const isDisabled = !checkPermissions(
    UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS
  );

  let content;
  let titleBar;

  if (deleteService) {
    if (hasBookings) {
      titleBar = (
        <Typography
          variant="title2Bold"
          sx={{
            background: 'linear-gradient(138deg, #FF9A9A, #FF5858)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('bookings.coachServicePackages.deleteWarning')}
        </Typography>
      );
    } else {
      titleBar = (
        <Typography
          variant="title2Bold"
          sx={{
            background: 'linear-gradient(138deg, #FF9A9A, #FF5858)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('bookings.coachServicePackages.confirmdeletion')}
        </Typography>
      );
    }
  } else {
    titleBar = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Tooltip
          title={title}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'primary.main',
                borderRadius: '5px',
                padding: '8px 12px',
              },
            },
          }}
        >
          <Typography
            variant="title2Bold"
            color="primary"
            textTransform="capitalize"
            sx={{
              cursor: 'pointer',
              display: '-webkit-box',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
            }}
          >
            {title}
          </Typography>
        </Tooltip>
        <Typography variant="bodyBold" color="primary" fontSize="15px">
          {bookingType === BookingTypeEnum.ONLINE ? t('bookings.online') : t('bookings.inPerson')}
        </Typography>
      </Box>
    );
  }

  if (deleteService) {
    if (hasBookings) {
      content = (
        <Box
          sx={{
            width: '100%',
            height: '76px',
            borderTop: '1px solid #EEEEEE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
          }}
        >
          <StyledBackButton
            sx={{
              paddingY: '9px !important',
            }}
            fullWidth
            onClick={handleCancel}
          >
            <span>{t('common.dismiss')}</span>
          </StyledBackButton>
        </Box>
      );
    } else {
      content = (
        <Box
          sx={{
            width: '100%',
            height: '80px',
            borderTop: '1px solid #EEEEEE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
            pt: 1,
            pb: 1,
          }}
        >
          <StyledBackButton
            sx={{
              paddingY: '9px !important',
            }}
            fullWidth
            onClick={handleCancel}
            disabled={deleteCoachServicePackageLoading}
          >
            <span>{t('bookings.coachServicePackages.cancel')}</span>
          </StyledBackButton>
          {deleteCoachServicePackageLoading ? (
            <LoadingButton paddingY={12} paddingX={62}>
              {t('bookings.coachServicePackages.confirm')}
            </LoadingButton>
          ) : (
            <Button
              variant="contained"
              sx={{
                background: 'var(--Brand-Cadet-Blue, linear-gradient(138deg, #FF9A9A, #FF5858))',
                borderRadius: '15px',
                '&:hover': {
                  background: 'var(--Brand-Cadet-Blue, linear-gradient(138deg, #FF9A9A, #FF5858))',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#d3d3d3',
                  color: '#fff',
                },
              }}
              fullWidth
              disabled={
                !checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS)
              }
              onClick={handleConfirmDelete}
            >
              {t('bookings.coachServicePackages.confirm')}
            </Button>
          )}
        </Box>
      );
    }
  } else {
    content = (
      <Box
        sx={{
          width: '100%',
          height: '80px',
          borderTop: '1px solid #EEEEEE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: 1,
          pb: 1,
        }}
      >
        <Button
          variant="contained"
          sx={{
            color: 'white !important',
            background:
              'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
            borderRadius: '15px',
            '&:hover': {
              background:
                'var(--Brand-Cadet-Blue, linear-gradient(138deg, #4e7c91 24.11%, #2d4f5f 83.2%))',
            },
            '&.Mui-disabled': {
              background: '#d3d3d3 !important',
              color: '#fff',
            },
          }}
          fullWidth
          disabled={
            (id === editModePackageId &&
              (createCoachServicePackagesLoading || updateCoachServicePackageLoading)) ||
            !checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS)
          }
          onClick={onEdit}
        >
          {t('bookings.coachServicePackages.edit')}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: {
          xs: '70%',
          sm: '70%',
          md: 'calc(50% - 16px)',
          lg: '333px',
        },
        width: {
          xs: 'calc(50% - 16px)',
          sm: 'calc(50% - 16px)',
          md: 'calc(33.33% - 16px)',
          lg: 'calc(25% - 16px)',
        },
        height: '500px',
        borderRadius: '10px',
        border: '1px solid #7CAEBE',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        background: backgroundColor ?? 'transparent',
      }}
    >
      <Box
        sx={{
          width: '100%',
          minHeight: '100px',
          borderRadius: '10px 10px 0 0',
          borderBottom: '1px solid #EEEEEE',
          padding: '24px',
          gap: '20px',
          position: 'relative',
        }}
      >
        {titleBar}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: 'calc(100% - 100px)',
          justifyContent: 'space-between',
          padding: '24px 24px 0 24px',
        }}
      >
        <Box
          sx={{
            gap: '10px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {deleteService && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Tooltip
                title={title}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'primary.main',
                      borderRadius: '5px',
                      padding: '8px 12px',
                    },
                  },
                }}
              >
                <Typography
                  variant="title2Bold"
                  color="primary"
                  textTransform="capitalize"
                  sx={{
                    cursor: 'pointer',
                    display: '-webkit-box',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                  }}
                >
                  {title}
                </Typography>
              </Tooltip>
              <Typography variant="bodyBold" color="primary" fontSize="15px">
                {bookingType === BookingTypeEnum.ONLINE
                  ? t('bookings.online')
                  : t('bookings.inPerson')}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              flexWrap: 'wrap',
              gap: '15px',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <img src="/assets/icons/service-price.svg" alt="price" width="24" height="24" />$
              {rate}
            </Typography>
            <Typography
              variant="h6"
              color="primary"
              sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <img src="/assets/icons/service-time.svg" alt="time" width="24" height="24" />
              {formatDuration(duration)}
            </Typography>
          </Box>

          <Typography
            variant="bodyRegular"
            color="primary"
            maxHeight={deleteService ? '110px' : '200px'}
            overflow="auto"
            sx={{
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              ...scrollbarStyles,
            }}
          >
            {description}
          </Typography>
        </Box>
        {content}
      </Box>
      <Box>
        <Button
          onClick={handleClickDelete}
          disabled={isDisabled}
          sx={{
            display: deleteService ? 'none' : 'flex',
            position: 'absolute',
            top: '2px',
            right: '6px',
            transform: 'translate(50%, -50%)',
            width: '24px',
            height: '24px',
            cursor: isDisabled ? 'none' : 'pointer',
            padding: '20px',
            transition: 'transform 0.3s ease, width 0.3s ease, height 0.3s ease',
            '&:hover': {
              background: 'transparent',
              transform: isDisabled ? 'none' : 'translate(50%, -50%) scale(1.2)',
            },
          }}
        >
          {isDisabled ? (
            <img src="/assets/icons/delete-service-grey.svg" alt="delete" width="24" height="24" />
          ) : (
            <img src="/assets/icons/delete-service.svg" alt="delete" width="24" height="24" />
          )}
        </Button>
      </Box>
    </Box>
  );
}
